import React, { useEffect, useState } from 'react';
import { SignalNode } from '../types';
import { SiteContent } from '../data/content';

interface SignalPathFlowProps {
  nodes: SignalNode[];
  heroContent: SiteContent['hero'];
}

export const SignalPathFlow: React.FC<SignalPathFlowProps> = ({ nodes, heroContent }) => {
  const [activeNodeIndex, setActiveNodeIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    if (!autoPlay || nodes.length < 2) return;

    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) {
      setAutoPlay(false);
      return;
    }

    const timer = window.setInterval(() => {
      setActiveNodeIndex((current) => (current + 1) % nodes.length);
    }, 3000);

    return () => window.clearInterval(timer);
  }, [autoPlay, nodes.length]);

  const activeNode = nodes[activeNodeIndex] ?? nodes[0];

  return (
    <div className="hero-object-card" role="region" aria-label={heroContent.flowLabel}>
      <div className="hero-object-top">
        <span className="hero-object-label">/ support-flow</span>
        <span className="hero-object-live">
          <span className="rec-dot is-live" aria-hidden="true" />
          live
        </span>
      </div>

      <div
        className="object-station-body"
        key={activeNode.id}
        aria-live="polite"
      >
        <div className="object-station-count">
          {activeNode.index}
          <span>/0{nodes.length}</span>
        </div>
        <p className="station-phase-tag">{activeNode.tag}</p>
        <h2 className="station-title">{activeNode.title}</h2>
        <p className="station-detail">{activeNode.detail}</p>
      </div>

      <div className="pipeline-rail" role="tablist" aria-label="Support signal path">
        {nodes.map((node, index) => {
          const active = index === activeNodeIndex;
          return (
            <button
              key={node.id}
              type="button"
              role="tab"
              aria-selected={active}
              className={`pipeline-node ${active ? 'is-active' : ''}`}
              onClick={() => {
                setActiveNodeIndex(index);
                setAutoPlay(false);
              }}
              aria-label={node.title}
            >
              <span className="pipeline-node-num">{node.index}</span>
              <span className="pipeline-node-dot" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      <div className="hero-object-bot">
        <span>{heroContent.signalPrompt}</span>
        <span aria-hidden="true">→</span>
        <span>{heroContent.systemResponsePrompt}</span>
      </div>
    </div>
  );
};
